import { lazy } from 'react';

/**
 * `React.lazy` that survives a deploy.
 *
 * Code splitting introduces one failure mode that a single bundle does not:
 * chunk filenames carry a content hash, so after a deploy the old `index.js`
 * still open in someone's tab asks for `VideoStudio-OLDHASH.js`, which no
 * longer exists. The import rejects, the Suspense boundary has nothing to
 * render, and the route goes blank.
 *
 * It is not hypothetical here. The service worker is registered with
 * `registerType: 'prompt'`, deliberately, so a tab can legitimately sit on an
 * old build for hours — through however many deploys — with unsaved work in
 * VideoStudio. That is exactly the window this covers.
 *
 * Two attempts, then one reload:
 *
 *   1. retry once. A chunk fetch can fail on a flaky connection, and a
 *      transient failure should not cost a page reload.
 *   2. reload. The old HTML is what is stale; reloading picks up the new
 *      `index.js` and with it the new chunk names.
 *
 * The reload is guarded by a sessionStorage flag so a genuinely missing chunk
 * — a broken deploy, a CDN serving a partial upload — cannot put the tab in a
 * reload loop. Second time through, the error is allowed to surface.
 */
const RELOAD_FLAG = 'os:chunk-reload';

export const lazyRoute = (importer) =>
  lazy(async () => {
    try {
      const mod = await importer();
      // Cleared only on SUCCESS, so the guard covers the whole recovery
      // attempt rather than being reset by the reload it just triggered.
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch (e) { /* private mode */ }
      return mod;
    } catch (first) {
      try {
        return await importer();
      } catch (second) {
        let alreadyTried = false;
        try {
          alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === '1';
          sessionStorage.setItem(RELOAD_FLAG, '1');
        } catch (e) {
          // No sessionStorage (private mode, blocked storage). Reloading
          // without the guard risks a loop, so don't: surface the error.
          alreadyTried = true;
        }
        if (!alreadyTried) {
          window.location.reload();
          // Never resolves; the reload is already under way and resolving
          // would flash an error the user is about to navigate away from.
          return new Promise(() => {});
        }
        throw second;
      }
    }
  });

export default lazyRoute;
